import TransmittalContactSelection, {ContactSelectionProps} from "./TransmittalContactSelection.tsx";
import * as React from "react";
import {TransmittalItemSelection} from "./TransmittalItemSelection.tsx";
import SparkMD5 from 'spark-md5'
import {Drawing, Document, AttachedFile, Staff} from "../Components/BaseTypes.ts";
import {useEffect, useRef, useState} from "react";
import axios from "../Utilities/axios.ts";
import {SendEmail, transmittalSendDto} from "./SendEmail.tsx";
import {ApprovalRequest} from "./TransmittalApproval.tsx";
import Toaster from "../Components/Toaster.tsx";
import {TransmittalPermissions} from "./types.ts";
import {Attachment} from "./Attachment.tsx";
import {sumArray} from "../Components/Utils.ts";


let _running = false;


function updateDataStructuresWithAttachedFiles(attachedFiles: AttachedFile[], availableDocumentsLookup: Record<number, Document>, availableDrawingsLookup: Record<number, Drawing>, otherAttachments: AttachedFile[])
    : [attachedFiles: AttachedFile[], availableDocumentsLookup: Record<number, Document>, availableDrawingsLookup: Record<number, Drawing>, otherAttachments: AttachedFile[]] {
    const allDocuments = Object.values(availableDocumentsLookup);
    const allDrawings = Object.values(availableDrawingsLookup);
    attachedFiles?.forEach((attachedFile) => {
        // if (!attachedFile.docId) attachedFile.docId = [];
        if (!attachedFile.dwgIds) attachedFile.dwgIds = [];


        const docThatMatchsAttachedFile = allDocuments.find(doc => {
            const docNoArr = doc.dorMelDocNo.split('-');
            // if (docNoArr.length > 4) docNoArr.splice(4, 1);
            if (docNoArr.length > 4) docNoArr[4] = '.*'

            return doc?.dorMelDocNo && attachedFile.attachedFileName.match(new RegExp(docNoArr.join('-')));
        });

        const dwgSeqMatches = attachedFile.attachedFileName.match(/\d*-\d*-\d*-\d*-((?<dwgSeq>\d)|(\((?<dwgSeqRange>.*)\)))-\d/);
        const dwgSeqRangeArr = dwgSeqMatches?.groups?.dwgSeqRange?.split('-') || [];
        const dwgListThatMatchesAttachedFile = allDrawings.filter(drawing => {
                const dwgNoArr = drawing.dwgMelDwgNo.split('-');

                //it's difficult/impossible to match drawing numbers to into specific formatted file names with a compound number of drawings indicated in the file name (e.g 2-10)
                //so we filter out and make sure the drawing sequence is valid first, then check everything else
                if (dwgSeqRangeArr.length == 2 && !(Number(dwgNoArr[4]) >= Number(dwgSeqRangeArr[0]) && Number(dwgNoArr[4]) <= Number(dwgSeqRangeArr[1]))) return false;

                dwgNoArr[4] = '.*';

                return (drawing.dwgMelDwgNo && attachedFile.attachedFileName.match(new RegExp(dwgNoArr.join('-')))) ||
                    (drawing.dwgCliDwgNo && attachedFile.attachedFileName.match(drawing.dwgCliDwgNo))
            }
        );

        if (!attachedFile.id) attachedFile.id = Math.floor(Math.random() * 100000000);
        attachedFile.isSaved = false;
        if (docThatMatchsAttachedFile) {
            attachedFile.docId = docThatMatchsAttachedFile.id;
            const attachmentToUpdateIndex = docThatMatchsAttachedFile.fileAttachments.findIndex(a => a.attachedFileName === attachedFile.attachedFileName);
            if (attachmentToUpdateIndex >= 0) {
                availableDocumentsLookup[docThatMatchsAttachedFile.id].fileAttachments[attachmentToUpdateIndex] =
                    {...availableDocumentsLookup[docThatMatchsAttachedFile.id].fileAttachments[attachmentToUpdateIndex], ...attachedFile}
            } else {
                availableDocumentsLookup[docThatMatchsAttachedFile.id].fileAttachments.push(attachedFile);
            }
        } else if (dwgListThatMatchesAttachedFile.length) {
            dwgListThatMatchesAttachedFile.map(dwgThatMatchesAttachedFile => {
                attachedFile.dwgIds.push(dwgThatMatchesAttachedFile.id);

                const attachmentToUpdateIndex = dwgThatMatchesAttachedFile.fileAttachments.findIndex(a => a.attachedFileName === attachedFile.attachedFileName);
                if (attachmentToUpdateIndex >= 0) {
                    availableDrawingsLookup[dwgThatMatchesAttachedFile.id].fileAttachments[attachmentToUpdateIndex] =
                        {...availableDrawingsLookup[dwgThatMatchesAttachedFile.id].fileAttachments[attachmentToUpdateIndex], ...attachedFile}
                } else {
                    availableDrawingsLookup[dwgThatMatchesAttachedFile.id].fileAttachments.push(attachedFile);
                }
            })

        } else {
            const attachmentToUpdateIndex = otherAttachments.findIndex(a => a.attachedFileName === attachedFile.attachedFileName);
            if (attachmentToUpdateIndex >= 0) {
                otherAttachments[attachmentToUpdateIndex] = {...otherAttachments[attachmentToUpdateIndex], ...attachedFile};
            } else {
                otherAttachments.push(attachedFile);
            }
        }
    })

    return [attachedFiles, availableDocumentsLookup, availableDrawingsLookup, otherAttachments];
}

function getTotalSizeForSelectedAttachedFiles(selectedDocumentIds: number[], selectedDrawingIds: number[], docsLookup: Record<number, Document>, dwgsLookup: Record<number, Drawing>, otherAttachments: AttachedFile[]) {
    console.log('**** get total size called');
    const uniqueAttachedFilesLookup = {};
    selectedDocumentIds.forEach((id => {
        const attachments = docsLookup[id]?.fileAttachments || [];
        attachments.forEach(attachment => {
            uniqueAttachedFilesLookup[attachment.attachedFileName] = attachment;
        })
    }))
    selectedDrawingIds.forEach((id => {
        const attachments = dwgsLookup[id]?.fileAttachments || [];
        attachments.forEach(attachment => {
            uniqueAttachedFilesLookup[attachment.attachedFileName] = attachment;
        })
    }))

    return sumArray([
        ...Object.values(uniqueAttachedFilesLookup),
        ...otherAttachments
    ].map((attachment: AttachedFile) => attachment.attachedFileSize));
}

// function verifyAttachedFileLocation(entityIdToAttachedFileLookup: Record<number, AttachedFile>) {
// }
interface TransmittalBaseProps extends ContactSelectionProps {
    selectedDrawingIds: number[],
    selectedDocumentIds: number[],
    availableDrawingsLookup: Record<number, Drawing>,
    availableDocumentsLookup: Record<number, Document>,
    transmittalAttachments: AttachedFile[],
    docTypesLookup: Record<number, object>,
    projectId: number,
    transmittalDoc: Document,
    trnMessage: string,
    sendEmailDto: transmittalSendDto,
    approvalStatusList: string[],
    approversLookup: Record<number, Staff>,
    currentStaff: Staff,
    approvalRequest: ApprovalRequest,
    transmittalStatus: 'Work In Progress' | 'Awaiting Approval' | 'Transmittal Issued',
    permissions: TransmittalPermissions,
    openSubmission?: boolean
}

export default function TransmittalBase({
                                            selectedDrawingIds,
                                            selectedDocumentIds,
                                            docTypesLookup,
                                            contactList,
                                            selectedContactIds,

                                            selectedReasonsForIssue,
                                            reasonsForIssueLookup,
                                            currentClientId,
                                            projectId,
                                            transmittalDoc,
                                            trnMessage,
                                            sendEmailDto,
                                            approversLookup,
                                            approvalStatusList,
                                            currentStaff,
                                            approvalRequest,
                                            transmittalStatus,
                                            openSubmission = false,
                                            permissions,
                                            ...props
                                        }: TransmittalBaseProps) {


    // const setupStateForAttachedFiles = () => {
    //     return {
    //         ...getItemCodeToAttachedFileLookupFromBaseEntities(selectedDrawingIds, availableDrawingsLookup),
    //         ...getItemCodeToAttachedFileLookupFromBaseEntities(selectedDocumentIds, availableDocumentsLookup)
    //     };
    // }
    // const [entityCodeToAttachedFileLookup, setEntityCodeToAttachedFileLookup] = useState<Record<string, AttachedFile>>({});
    const [availableDrawingsLookup, setAvailableDrawingsLookup] = useState(props.availableDrawingsLookup);
    const [availableDocumentsLookup, setAvailableDocumentsLookup] = useState(props.availableDocumentsLookup);
    const [otherAttachments, setOtherAttachments] = useState(props.transmittalAttachments);
    const [totalAttachedFileSize, setTotalAttachedFileSize] = useState(() =>
        getTotalSizeForSelectedAttachedFiles(selectedDocumentIds, selectedDrawingIds, availableDocumentsLookup, availableDrawingsLookup, otherAttachments))

    //25MB attachment danger level
    const attachedFileSizeFlag = Number((Number(totalAttachedFileSize) / 1024 / 1024).toFixed(1)) > 25;

    const currentSelectedDrawingsRef = useRef();
    const currentSelectedDocumentsRef = useRef();

    useEffect(() => {
        if (!(currentSelectedDrawingsRef.current && currentSelectedDocumentsRef.current)) return
        // @ts-expect-error will be defined by this point
        setTotalAttachedFileSize(getTotalSizeForSelectedAttachedFiles(currentSelectedDocumentsRef.current.getSelectedIds(), currentSelectedDrawingsRef.current.getSelectedIds(), availableDocumentsLookup, availableDrawingsLookup, otherAttachments));
    }, [availableDrawingsLookup, availableDocumentsLookup])

    const disabled = transmittalStatus === 'Transmittal Issued';

    const approvalDto = {
        value: approvalRequest,
        currentStaffId: currentStaff.id,
        disabled: false,
        required: true,
        approversLookup: approversLookup,
        statusList: approvalStatusList,
    }


    return <div
        style={{height: '100%', overflow: 'auto'}}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={(e) => e.preventDefault()}
        onDrop={(e) => {
            if (e?.dataTransfer?.files?.length) {
                e.preventDefault();
                getAttachedFilesWithHashes(Array.from(e.dataTransfer.files)).then(attachedFiles => {
                    const [updatedAttachedFiles, updatedDocumentsLookup, updatedDrawingsLookup, updatedOtherAttachments] = updateDataStructuresWithAttachedFiles(attachedFiles, availableDocumentsLookup, availableDrawingsLookup, otherAttachments);


                    // @ts-expect-error this only happens after the ref is loaded
                    currentSelectedDocumentsRef.current.updateItemsFromAttachedItemIds(updatedAttachedFiles
                        .filter(attached => attached.docId)
                        .map(attached => attached.docId));
                    // @ts-expect-error this only happens after the ref is loaded
                    currentSelectedDrawingsRef.current.updateItemsFromAttachedItemIds(updatedAttachedFiles
                        .filter(attached => attached.dwgIds.length)
                        .map(attached => attached.dwgIds).flat(1));

                    setAvailableDocumentsLookup({...updatedDocumentsLookup});
                    setAvailableDrawingsLookup({...updatedDrawingsLookup});
                    setOtherAttachments([...updatedOtherAttachments]);


                    if (updatedAttachedFiles.length) {
                        //@ts-expect-error Function defined as html include
                        handleChangeEvent();
                        axios.post('/transmittal/verifyAttachedFiles', {
                            projectId: projectId,
                            attachedFiles: updatedAttachedFiles,
                        })
                            .then((responseJson) => {
                                    const verifiedAddedAttachedFiles: AttachedFile[] = responseJson.data;
                                    const [updatedAttachedFiles, updatedDocumentsLookup, updatedDrawingsLookup, updatedOtherAttachments] =
                                        updateDataStructuresWithAttachedFiles(verifiedAddedAttachedFiles, availableDocumentsLookup, availableDrawingsLookup, otherAttachments);


                                    setAvailableDocumentsLookup({...updatedDocumentsLookup});
                                    setAvailableDrawingsLookup({...updatedDrawingsLookup});
                                    setOtherAttachments([...updatedOtherAttachments]);

                                }
                            )
                            .catch(error => window.alert('Failed to verify attachments, please make sure you are on the internet and logged on to the kiosk'))

                    }
                    // const invalidAttachments: AttachedFile[] = attachedFiles.filter(attachedFile => !attachedFile.entityId);
                });
            }
        }}
    >
        <Toaster/>

        <div className="ps-1 d-flex align-items-center justify-content-between gap-1"
             style={{backgroundColor: '#CCEEEE'}}>
            <div className="d-flex flex-wrap gap-2">
                <div>Create transmittal document:</div>
                <div className="border border-dark">
                    <a target="_blank" href="/document/procedure_doc/MSDOC.013"
                       className="procedureDocIcon border-0">
                        <span className="px-1">MSDOC.013 Guide to Kiosk Document Transmittals</span>
                        <span className="icon" style={{fontFamily: 'wingdings3'}}></span>
                    </a>
                </div>
            </div>
            <div className="d-flex gap-1">
                <SendEmail
                    {...sendEmailDto}
                    contactList={contactList}
                    rootUrl={'/transmittal/' + sendEmailDto.transmittalId}
                    approvalDto={approvalDto}
                    open={openSubmission}
                    currentStaffEmail={currentStaff.stfBusEmail}
                    sendEnabled={permissions?.submitEnabled}
                    approveEnabled={permissions?.approveEnabled}
                    customRecipientEmailAddresses={props.selectedCustomEmailRecipients}
                />
                <input type="button" value="Cancel" onClick={() => history.go(-1)} id="cancel"
                       name="cancel" className="button"/>
                <input type="submit" name="transmittal_save" value="Save" form='frm_trans_edit' disabled={disabled}
                       onClick={() => window.onbeforeunload = null}
                       className="submitSave button"/>
            </div>
        </div>
        <form name="frm_trans_edit" id='frm_trans_edit' method="post"
            //@ts-expect-error Function defined as html include
              onChange={(e) => console.log('change at trans', e) || handleChangeEvent()}
        >
            <input type="hidden" name="transID" value={transmittalDoc.id}/>
            <input type="hidden" name="pk_transID" value={sendEmailDto.transmittalId}/>
            <fieldset disabled={disabled}>
                <br/>
                <div className="d-flex">
                    <div className="flex-grow-1 gap-2">
                        <table className="styled w-100">
                            <tr>
                                <th style={{width: '10em'}}>Transmittal No</th>
                                <td>{transmittalDoc.dorMelDocNo}</td>
                                <th style={{width: '10em'}}>Created By</th>
                                <td>{transmittalDoc.dorNoCreatedby}</td>
                                <th style={{width: '10em'}}>Date Created</th>
                                <td>{transmittalDoc.dateCreated}</td>
                            </tr>
                            <tr>
                                <th style={{width: '10em'}}>Client</th>
                                <td>{transmittalDoc.fkClientid?.cliCompanyname || ''}</td>
                                <th style={{width: '10em'}}>Project Ref No</th>
                                <td>{`${transmittalDoc.fkProjectid?.proRef || ''} ${transmittalDoc.fkProjectid?.proTitle || ''}`} </td>
                                <th style={{width: '10em'}}>Job Ref No</th>
                                <td>{`${transmittalDoc.fkJobid?.jobRef || ''} ${transmittalDoc.fkJobid?.jobTitle || ''}`} </td>
                            </tr>
                            <tr>
                                <th>Transmittal Status</th>
                                <td>
                                    {transmittalStatus}
                                    {transmittalStatus === 'Transmittal Issued' && ` (${sendEmailDto.dateEmailed})`}
                                </td>
                                {/*<td colSpan={100}>*/}
                                {/*    {ApprovalRowRequired}*/}
                                {/*</td>*/}
                            </tr>

                        </table>

                        <br/>
                        <table className='styled w-100'>
                            <tr>
                                <th style={{width: '10em'}}>Message:</th>
                                <td className="p-0">
                    <textarea
                        className="w-100"
                        required={true}
                        name="trans_notes" rows={8} wrap="VIRTUAL"
                    >{trnMessage}</textarea>
                                </td>

                            </tr>

                        </table>
                    </div>
                    {/*<div style={{width: '40em'}}>*/}
                    {/*    {ApprovalRow}*/}
                    {/*</div>*/}
                </div>


                <br/>
                <TransmittalContactSelection
                    selectedCustomEmailRecipients={props.selectedCustomEmailRecipients}
                    contactList={contactList}
                    currentClientId={currentClientId}
                    reasonsForIssueLookup={reasonsForIssueLookup}
                    selectedReasonsForIssue={selectedReasonsForIssue}
                    selectedContactIds={selectedContactIds}
                />
                <br/>

                <div style={{
                    margin: '5px 0 0 0',
                    lineHeight: '2.2em',
                    backgroundColor: '#EEEEEE'
                }}
                     className="d-flex justify-content-between align-items-center p-2">
                    <h4>Attachments</h4>

                    <div className="d-flex align-items-center">
                        {attachedFileSizeFlag && <span className="p-1 small text-body-secondary">Warning: Some recipients may have trouble receiving a transmittal over 25MB</span>}
                        <span
                            style={{padding: '0.3em', marginRight: '-0.3em'}}
                            className={`text-body-secondary small text-nowrap ${attachedFileSizeFlag && 'bg-warning'}`}>{(Number(totalAttachedFileSize) / 1024 / 1024).toFixed(2)} MB</span>
                        <div style={{padding: '0.5em', marginLeft: '0.5em'}}>
                            <div style={{width: '25px'}}></div>
                        </div>
                    </div>
                </div>
                <h5 style={{
                    margin: '5px 0 0 0',
                    paddingLeft: '0.5em',
                    lineHeight: '2.2em',
                    backgroundColor: '#EEEEEE'
                }}>Other Attachments</h5>
                <div className='p-2'>
                    {!!otherAttachments.length ?
                        <>
                            {
                                otherAttachments.map(attachment =>
                                    <div className="d-flex justify-content-between">
                                        <Attachment attachment={attachment} onDelete={() => {
                                            setOtherAttachments([...otherAttachments.filter(v => v.id != attachment.id)])
                                            //@ts-expect-error Function defined as html include
                                            handleChangeEvent();
                                        }}/>
                                        <div style={{padding: '0.5em', marginLeft: '0.5em'}}>
                                            <div style={{width: '25px'}}></div>
                                        </div>
                                    </div>
                                )
                            }
                        </>
                        : <span>No other attachments have been added</span>
                    }
                </div>
                <br/>
                <TransmittalItemSelection
                    onChange={(v) =>
                        setTotalAttachedFileSize(getTotalSizeForSelectedAttachedFiles(currentSelectedDocumentsRef.current.getSelectedIds(), v.map(v => v.id), availableDocumentsLookup, availableDrawingsLookup, otherAttachments))
                    }
                    ref={currentSelectedDrawingsRef}
                    selectedDrawingIds={selectedDrawingIds}
                    availableDrawingsLookup={availableDrawingsLookup}
                    availableEntitySetter={setAvailableDrawingsLookup}
                />
                <br/>
                <TransmittalItemSelection
                    onChange={(v) =>
                        setTotalAttachedFileSize(getTotalSizeForSelectedAttachedFiles(v.map( v=> v.id), currentSelectedDrawingsRef.current.getSelectedIds(), availableDocumentsLookup, availableDrawingsLookup, otherAttachments))
                    }
                    ref={currentSelectedDocumentsRef}
                    selectedDocumentIds={selectedDocumentIds}
                    availableDocumentsLookup={availableDocumentsLookup}
                    docTypesLookup={docTypesLookup}
                    availableEntitySetter={setAvailableDocumentsLookup}
                />
            </fieldset>
        </form>
    </div>

}

async function getAttachedFilesWithHashes(files: File[]): Promise<AttachedFile[]> {
    if (_running) {
        return;
    }

    if (!files?.length) {
        return;
    }

    const promises = [];
    files.forEach(file => {
        promises.push(new Promise((resolve, reject) => {
                const blobSlice = File.prototype.slice,
                    chunkSize = 2097152,                             // Read in chunks of 2MB
                    chunks = Math.ceil(file.size / chunkSize),
                    spark = new SparkMD5.ArrayBuffer(),
                    fileReader = new FileReader();

                let currentChunk = 0;


                fileReader.onload = function (e) {
                    spark.append(e.target.result);                   // Append array buffer
                    currentChunk++;

                    if (currentChunk < chunks) {
                        loadNext();
                    } else {
                        _running = false;
                        const hash = spark.end();
                        resolve({
                            attachedFileSize: file.size,
                            attachedFileName: file.name,
                            verifiedFilePath: '',
                            attachedFileChecksum: hash
                        })
                        console.info('computed hash', hash);  // Compute hash
                    }
                };

                fileReader.onerror = function () {
                    _running = false;
                    console.warn('oops, something went wrong.');
                    reject();
                };

                function loadNext() {
                    const start = currentChunk * chunkSize,
                        end = ((start + chunkSize) >= file.size) ? file.size : start + chunkSize;

                    fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
                }

                _running = true;

                loadNext();
            })
        )
    })

    return await Promise.all(promises);

}